/**
 * Metric category colors — widget-local copy (no packages/ui import in CDN bundle).
 * Algorithm ported from the original CoffeeScript plumprint/print.coffee.
 *
 * SYNC NOTE: Core blob algorithm (buildAtoms / buildShapePath / constants) is intentionally
 * duplicated from packages/ui/src/components/PlumPrint/PlumPrint.tsx because the widget
 * ships as a standalone IIFE bundle. Keep both in sync when modifying the algorithm.
 */
export const METRIC_COLORS = {
  citation: '#fd5704',
  capture: '#c43bf3',
  mention: '#f3c316',
  socialMedia: '#13b7ea',
  usage: '#6dd54d',
} as const;

export type MetricColorKey = keyof typeof METRIC_COLORS;

// ── Legacy constants (ported from plumprint/print.coffee) ─────────────────────
const BG_COLOR = '#6e1a62';
const RING_COLOR = '#ffffff';
const RENDER_ORDER: MetricColorKey[] = ['citation', 'socialMedia', 'mention', 'capture', 'usage'];
const STEP = 360 / RENDER_ORDER.length; // 72°
const MIN_RADIUS = 5;
const MAX_RADIUS = 14;
const MIN_PADDING = 5;
const BASE_DIST = 14;
const ANGLE_MOD = 122;
const DIST_FACT = 0.2;
const ROTATION_DEG = -90;

const toRad = (deg: number) => (deg * Math.PI) / 180;
const log2 = (n: number) => Math.log(n) / Math.LN2;

interface AtomData {
  metric: MetricColorKey;
  count: number;
  x: number;
  y: number;
  radius: number;
  distance: number;
  angle: number;
  isEmpty: boolean;
}

export const buildAtoms = (
  metrics: Record<MetricColorKey, number>,
  cx: number,
  cy: number,
): AtomData[] =>
  RENDER_ORDER.map((metric, i) => {
    const count = metrics[metric] ?? 0;
    const isEmpty = count <= 0;
    const radius = isEmpty
      ? MIN_RADIUS - 1
      : Math.max(MIN_RADIUS, Math.min(MAX_RADIUS, log2(count) + MIN_RADIUS));
    const padding = isEmpty ? MIN_PADDING - 1 : (radius - MIN_PADDING) * 1.3 + MIN_PADDING;
    const distance = BASE_DIST + padding;
    const angle = STEP * (i - 1) + ROTATION_DEG; // legacy off-by-one plus 90deg clockwise rotation
    const rad = toRad(angle);
    return {
      metric,
      count,
      x: Math.sin(rad) * distance + cx,
      y: Math.cos(rad) * distance + cy,
      radius,
      distance,
      angle,
      isEmpty,
    };
  });

const getMergePoints = (atom: AtomData) =>
  ([atom.angle - 125, atom.angle + 125] as const).map(a => {
    const r = toRad(a);
    return { x: Math.sin(r) * atom.radius + atom.x, y: Math.cos(r) * atom.radius + atom.y };
  }) as [{ x: number; y: number }, { x: number; y: number }];

const getCtrlPoints = (atom: AtomData, cx: number, cy: number) =>
  ([atom.angle + 180 - ANGLE_MOD, atom.angle + 180 + ANGLE_MOD] as const).map(a => {
    const r = toRad(a);
    return {
      x: Math.sin(r) * atom.distance * DIST_FACT + cx,
      y: Math.cos(r) * atom.distance * DIST_FACT + cy,
    };
  }) as [{ x: number; y: number }, { x: number; y: number }];

const f = (n: number) => n.toFixed(2);

const buildShapePath = (atoms: AtomData[], cx: number, cy: number): string => {
  const mps = atoms.map(getMergePoints);
  const cps = atoms.map(a => getCtrlPoints(a, cx, cy));
  const n = atoms.length;
  const parts: string[] = [];

  for (let i = 0; i < n; i++) {
    const next = (i + 1) % n;
    const [, lmp] = mps[i]!;
    const [, lcp] = cps[i]!;
    const [fcp] = cps[next]!;
    const nfmp = mps[next]![0]!;
    parts.push(i === 0 ? `M ${f(lmp.x)},${f(lmp.y)}` : `L ${f(lmp.x)},${f(lmp.y)}`);
    parts.push(`C ${f(lcp.x)},${f(lcp.y)} ${f(fcp.x)},${f(fcp.y)} ${f(nfmp.x)},${f(nfmp.y)}`);
  }
  parts.push('Z');
  return parts.join(' ');
};

// ── Component ─────────────────────────────────────────────────────────────────
const SIZE_MAP = { xs: 18, sm: 32, md: 64, lg: 120 } as const;

interface PlumPrintProps {
  metrics: {
    citation: number;
    usage: number;
    capture: number;
    mention: number;
    socialMedia: number;
  };
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

const PlumPrint = ({ metrics, size = 'md' }: PlumPrintProps) => {
  const px = SIZE_MAP[size];
  const atoms = buildAtoms(metrics as Record<MetricColorKey, number>, 50, 50);
  const path = buildShapePath(atoms, 50, 50);

  return (
    <svg
      viewBox="0 0 100 100"
      width={px}
      height={px}
      aria-label="PlumX metrics visualization"
      role="img"
      overflow="visible"
      style={{ display: 'block' }}
    >
      <path d={path} fill={BG_COLOR} stroke={BG_COLOR} strokeWidth={0.5} />
      {atoms.map(atom =>
        atom.isEmpty ? (
          <circle key={atom.metric} cx={atom.x} cy={atom.y} r={atom.radius} fill={BG_COLOR} />
        ) : (
          <g key={atom.metric}>
            <circle
              cx={atom.x}
              cy={atom.y}
              r={atom.radius}
              fill={METRIC_COLORS[atom.metric]}
              stroke={BG_COLOR}
              strokeWidth={0.5}
            />
            <circle
              cx={atom.x}
              cy={atom.y}
              r={atom.radius - 1.5}
              fill="none"
              stroke={RING_COLOR}
              strokeWidth={1.5}
            />
          </g>
        ),
      )}
    </svg>
  );
};

export default PlumPrint;
